import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllUsers, updateUserRole } from '../slice/usersSlice';
import { fetchAllOrganizations, updateUserOrganization } from '../slice/organizationsSlice';
import { useNavigate } from 'react-router-dom';
import ADD from './settings/assets/add.svg';
import { useToast } from '../components/Toast';

const OrganizationModal = ({ isOpen, onClose, onConfirm, organizations = [], currentOrganization = null }) => {
  const [selectedOrganization, setSelectedOrganization] = useState(currentOrganization);

  useEffect(() => {
    setSelectedOrganization(currentOrganization);
  }, [currentOrganization]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[#D6D6D6B2] bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96">
        <h2 className="text-xl font-semibold mb-4">Organisation zuweisen:</h2>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Organisation auswählen
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-custom-yellow"
            value={selectedOrganization || ''}
            onChange={(e) => setSelectedOrganization(e.target.value || null)}
          >
            <option value="">Keine Organisation</option>
            {organizations.map((org) => (
              <option key={org.organization_id} value={org.organization_id}>
                {org.organization_name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            onClick={onClose}
          >
            Abbrechen
          </button>
          <button
            className="px-4 py-2 text-sm font-medium text-white bg-custom-yellow rounded-md hover:bg-yellow-500"
            onClick={() => {
              onConfirm(selectedOrganization);
              onClose();
            }}
          >
            Änderungen speichern
          </button>
        </div>
      </div>
    </div>
  );
};

const RoleModal = ({ isOpen, onClose, onConfirm, currentRole, userName }) => {
  const [selectedRole, setSelectedRole] = useState(currentRole);
  const roleOptions = ['No License', 'normal', 'ADMIN', 'SUPER_ADMIN'];

  useEffect(() => {
    setSelectedRole(currentRole);
  }, [currentRole]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[#D6D6D6B2] bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96">
        <h2 className="text-xl font-semibold mb-4">Nutzerrolle anpassen:</h2>
        <div className="mb-4">
          <div className="mb-4">
            <span className="text-sm text-gray-600">Nutzer: </span>
            <span className="font-medium">{userName}</span>
          </div>
          <div className="mb-4">
            <span className="text-sm text-gray-600">Aktuelle Rolle: </span>
            <span className={`px-2 py-1 text-xs rounded-full inline-block ${
              currentRole === 'ADMIN'
                ? 'bg-purple-100 text-purple-800'
                : currentRole === 'SUPER_ADMIN'
                ? 'bg-red-100 text-red-800'
                : currentRole === 'No License'
                ? 'bg-gray-100 text-gray-800'
                : 'bg-custom-yellow text-white'
            }`}>
              {currentRole}
            </span>
          </div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Neue Rolle auswählen
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-custom-yellow"
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
          >
            {roleOptions.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            onClick={onClose}
          >
            Abbrechen
          </button>
          <button
            className="px-4 py-2 text-sm font-medium text-white bg-custom-yellow rounded-md hover:bg-yellow-500"
            onClick={() => {
              onConfirm(selectedRole);
              onClose();
            }}
            disabled={selectedRole === currentRole}
          >
            Änderungen speichern
          </button>
        </div>
      </div>
    </div>
  );
};

const Users = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const users = useSelector((state) => state.all.users);
  const status = useSelector((state) => state.all.status);
  const error = useSelector((state) => state.all.error);
  const organizations = useSelector((state) => state.organizations.organizations);
  const organizationsStatus = useSelector((state) => state.organizations.status);

  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [organizationModalOpen, setOrganizationModalOpen] = useState(false);

  const { data, loading } = useSelector((state) => state.admin);
  const isAdmin = data?.user_role === "SUPER_ADMIN";
  const { addToast, ToastContainer } = useToast();

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!loading && data) {
        if (!isAdmin) {
          // Check if user has "No License" role
          if (data?.user_role === 'No License') {
            navigate('/hilfe');
            return;
          }
          navigate('/');
          return;
        }
        dispatch(fetchAllUsers());
        dispatch(fetchAllOrganizations());
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [dispatch, navigate, loading, isAdmin, data]);

  if (loading || !data) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  const handleRoleChange = async (newRole) => {
    if (!selectedUser) return;
    try {
      await dispatch(updateUserRole({
        userEmail: selectedUser.user_email,
        newRole
      })).unwrap();
      setRoleModalOpen(false);
      setSelectedUser(null);
      addToast("Rolle erfolgreich aktualisiert", "success");
    } catch (err) {
      console.error('Failed to update role:', err);
      addToast("Fehler beim Aktualisieren der Rolle", "error");
    }
  };

  const handleOrganizationChange = async (organizationId) => {
    if (!selectedUser) return;
    try {
      await dispatch(updateUserOrganization({
        user_email: selectedUser.user_email,
        organization_id: organizationId
      })).unwrap();
      setOrganizationModalOpen(false);
      setSelectedUser(null);
      dispatch(fetchAllUsers()); // Refresh users to get updated organization names
      addToast("Organisation erfolgreich zugewiesen", "success");
    } catch (err) {
      console.error('Failed to update organization:', err);
      addToast("Fehler beim Zuweisen der Organisation", "error");
    }
  };



  const openRoleModal = (user) => {
    setSelectedUser(user);
    setRoleModalOpen(true);
  };

  const openOrganizationModal = (user) => {
    setSelectedUser(user);
    setOrganizationModalOpen(true);
  };

  if (status === 'failed') {
    return (
      <div className="p-4 text-center">
        <p className="text-red-500">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="mb-16 pt-5 mt-8 mx-auto px-4 sm:px-6 rounded lg:px-8">
      <ToastContainer />
      <div className="pl-32 pr-40">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-semibold mb-6">Nutzer verwalten</h1>
          </div>
        </div>
        <div className="overflow-y-auto max-h-[60vh] no-scrollbar">
          <table className="min-w-full bg-white border border-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Full Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.entra_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {user.user_fullname}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {user.user_email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        user.user_role === 'ADMIN'
                          ? 'bg-purple-100 text-purple-800'
                          : user.user_role === 'SUPER_ADMIN'
                          ? 'bg-red-100 text-red-800'
                          : user.user_role === 'No License'
                          ? 'bg-gray-100 text-gray-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {user.user_role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {user.organization_name || 'Keine Organisation'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openRoleModal(user)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        Rolle anpassen
                      </button>
                      <button
                        onClick={() => openOrganizationModal(user)}
                        className="text-green-600 hover:text-green-900"
                      >
                        Organisation zuweisen
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Modals */}
      <RoleModal
        isOpen={roleModalOpen}
        onClose={() => {
          setRoleModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleRoleChange}
        currentRole={selectedUser?.user_role || 'normal'}
        userName={selectedUser?.user_fullname}
      />
      
      <OrganizationModal
        isOpen={organizationModalOpen}
        onClose={() => {
          setOrganizationModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleOrganizationChange}
        organizations={organizations}
        currentOrganization={selectedUser?.organization_id}
      />
      

    </div>
  );
};

export default Users;
